import { combineReducers, Reducer } from 'redux';
import { RouterState } from 'redux-first-history';
import { createResponsiveStateReducer } from 'redux-responsive';
import { ThemeBreakpoints } from '@dev/ee-components';
import { reduceMultiProductReducers } from '@shared-store/shared-store';

import * as ApiReducers from 'emailApp/reducers'; /* eslint-disable-line import/no-unresolved */
import singleLeads from 'evsApp/singleLeads/reducer'; /* eslint-disable-line import/no-unresolved */
import verificationFiles from 'evsApp/verificationFiles/reducer'; /* eslint-disable-line import/no-unresolved */
import { AccountState } from './account/types';
import { ActivityInterface } from './activity/types';
import { account } from './account/reducer';
import { activity } from './activity/reducer';
import { upload } from './upload/reducer';
import { UploadState } from './upload/types';
import { userFiles } from './userfiles/reducer';
import { UserFilesState } from './userfiles/types';
import { users } from './users/reducer';
import { UsersState } from './users/types';
import { contacts } from './contacts/reducer';
import { ContactsInterface } from './contacts/types';
import { customFields } from './customFields/reducer';
import { CustomFieldsInterface } from './customFields/types';
import { campaigns } from './campaigns/reducer';
import { CampaignsInterface } from './campaigns/types';
import { lists } from './lists/reducer';
import { ListsState } from './lists/types';
import { segments } from './segments/reducer';
import { SegmentsState } from './segments/types';
import { reports } from './reports/reducer';
import { ReportsState } from './reports/types';
import { emails } from './emails/reducer';
import { EmailsState } from './emails/types';
import { toast } from './toast/reducer';
import { ToastState } from './toast/types';
import { dialog } from './dialog/reducer';
import { DialogState } from './dialog/types';
import { webhooks } from './webhooks/reducer';
import { inbound } from './inboundRoutes/reducer';
import { WebhooksState } from './webhooks/types';
import { InboundState } from './inboundRoutes/types';
import { PoolsState } from './pools/types';
import { pools } from './pools/reducer';
import { SecurityState } from './security/types';
import { security } from './security/reducer';
import { ValidEmailsState } from './validemails/types';
import { validEmails } from './validemails/reducer';
import { session } from './session/reducer';
import { SessionState } from './session/types';
import { config } from './config/reducer';
import { domain } from './domain/reducer';
import { DomainState } from './domain/types';
import { SubAccountsState } from './subAccounts/types';
import { subAccounts } from './subAccounts/reducer';
import { exports } from './exports/reducer';
import { ExportsState } from './exports/types';
import { rawEditor } from './raweditor/reducer';
import { RawEditorState } from './raweditor/types';
import { templateList } from './templateList/reducer';
import { TemplateListState } from './templateList/types';
import { landingPageList } from './landingPageList/reducer';
import { templateTagList } from './templateTagList/reducer';
import { TemplateTagListState } from './templateTagList/types';
import { mediaManager } from './mediaManager/reducer';
import { MediaManagerState } from './mediaManager/types';
import { locationHistory } from './locationHistory/reducer';
import { LocationHistoryState } from './locationHistory/types';
import { products } from './products/reducer';
import { ProductListState } from './products/types';
import { automations } from './automations/reducer';
import { AutomationsInterface } from './automations/types';
import { NotificationStore } from './notifications/types';
import { notification } from './notifications/reducer';
import { FormListState } from './forms/types';
import { formList } from './forms/reducer';
import { legal } from './legal/reducer';
import { LegalState } from './legal/types';
import { channels } from './channels/reducer';
import { campaignCreator } from './campaignCreator/reducer';
import { templateEditor } from './templateEditor/reducer';
import { integrations } from './integrations/reducer';
import { dashboardSettings } from './dashboardSettings/reducer';
import { DashboardSettingsState } from './dashboardSettings/types';

import { PaymentsState } from './payments/types';
import { payments } from './payments/reducer';
import { onboardingSurvey } from './onboardingSurvey/reducer';

import { BrandColorsState } from './brandcolors/types';
import { brandColors } from './brandcolors/reducer';
import { prebuildModules } from './prebuildModules/reducer';

/* eslint-disable import/no-unresolved */
import { ConfigState } from './config/types'; /* eslint-disable import/no-unresolved */
import { CampaignCreatorState } from './campaignCreator/types';
import { TemplateEditorState } from './templateEditor/types';
import { OnboardingSurveyState } from './onboardingSurvey/types';
import { IntegrationsState } from './integrations/types';
import { PrebuildModulesState } from './prebuildModules/types';
import { aiText } from './aiText/reducer';
import { AiTextState } from './aiText/types';
import { dashboardSeenNotifications } from './dashboardSeenNotifications/reducer';
import { DashboardSeenNotificationsState } from './dashboardSeenNotifications/types';
import { OnboardingState } from './onboarding/types';
import { onboarding } from './onboarding/reducer';
import { FormCreatorState } from './formCreator/types';
import { formCreator } from './formCreator/reducer';
import { MergeFieldsState } from './mergeFields/types';
import { mergeFields } from './mergeFields/reducer';
import { landingPageCreator } from './landingPageCreator/reducer';
import { LandingPageCreatorState } from './landingPageCreator/types';
import { LandingPageSubdomain } from './landingPageSubdomain/types';
import { landingPageSubdomain } from './landingPageSubdomain/reducer';
import { checkoutCreator } from './checkoutCreator/reducer';
import { CheckoutCreatorState } from './checkoutCreator/types';
import { checkoutsList } from './checkouts/reducer';
import { CheckoutListState } from './checkouts/types';
import { CheckoutsTermsState } from './checkoutTerms/types';
import { checkoutsTerms } from './checkoutTerms/reducer';
import { CheckoutsStripeAccountState } from './checkoutsStripeAccount/types';
import { checkoutsStripeAccount } from './checkoutsStripeAccount/reducer';

export interface ApplicationState {
    account?: AccountState;
    error?: any;
    activity?: ActivityInterface;
    automations?: AutomationsInterface;
    upload?: UploadState;
    userFiles?: UserFilesState;
    users?: UsersState;
    lists?: ListsState;
    segments?: SegmentsState;
    contacts?: ContactsInterface;
    toast?: ToastState;
    dialog?: DialogState;
    router?: RouterState;
    browser?: BrowserInterface;
    reports?: ReportsState;
    rawEditor?: RawEditorState;
    templateList?: TemplateListState;
    templateTagList?: TemplateTagListState;
    formList?: FormListState;
    landingPageList?: TemplateListState;
    domain?: DomainState;
    pools?: PoolsState;
    inbound?: InboundState;
    webhooks?: WebhooksState;
    security?: SecurityState;
    validEmails?: ValidEmailsState;
    session: SessionState;
    config: ConfigState;
    campaigns?: CampaignsInterface;
    customFields?: CustomFieldsInterface;
    emails: EmailsState;
    subAccounts: SubAccountsState;
    exports: ExportsState;
    mediaManager?: MediaManagerState;
    locationHistory?: LocationHistoryState;
    products?: ProductListState;
    notification?: NotificationStore;
    channels: any;
    statistics: any;
    logs: any;
    deviceDetails: any;
    suppressions: any;
    // emailapiCampaigns: CampaignsState;
    singleLeads: any;
    verificationFiles: any;
    legal: LegalState;
    campaignCreator: CampaignCreatorState;
    payments: PaymentsState;
    templateEditor: TemplateEditorState;
    onboardingSurvey: OnboardingSurveyState;
    integrations: IntegrationsState;
    brandColors: BrandColorsState;
    dashboardSettings: DashboardSettingsState;
    prebuildModules: PrebuildModulesState;
    aiText: AiTextState;
    dashboardSeenNotifications: DashboardSeenNotificationsState;
    onboarding: OnboardingState;
    formCreator: FormCreatorState;
    mergeFields: MergeFieldsState;
    landingPageCreator: LandingPageCreatorState;
    landingPageSubdomain: LandingPageSubdomain;
    checkoutCreator: CheckoutCreatorState;
    checkoutsList?: CheckoutListState;
    checkoutsTerms?: CheckoutsTermsState;
    checkoutsStripeAccount?: CheckoutsStripeAccountState;
}

export interface BrowserInterface {
    mediaType: string;
    orientation: string;
    lessThan: {
        basicContainer: boolean;
        container: boolean;
        xs: boolean;
        sm: boolean;
        hd: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };
    greaterThan: {
        basicContainer: boolean;
        container: boolean;
        xs: boolean;
        sm: boolean;
        hd: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };
    is: {
        basicContainer: boolean;
        container: boolean;
        xs: boolean;
        sm: boolean;
        hd: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };
}

export const { xl, basicContainer, container, ...screenSizes } = ThemeBreakpoints;

export const reducers = (routerReducer: Reducer<RouterState>): Reducer<ApplicationState> => {
    const productReducers = {
        account,
        activity,
        automations,
        upload,
        users,
        userFiles,
        lists,
        segments,
        exports,
        contacts,
        toast,
        dialog,
        router: routerReducer,
        reports,
        templateList,
        templateTagList,
        formList,
        landingPageList,
        rawEditor,
        config,
        pools,
        inbound,
        webhooks,
        security,
        validEmails,
        campaigns,
        customFields,
        emails,
        session,
        browser: createResponsiveStateReducer(screenSizes as any, { infinity: 'xl' }),
        domain,
        subAccounts,
        mediaManager,
        locationHistory,
        products,
        notification,
        legal,
        channels,
        campaignCreator,
        payments,
        templateEditor,
        onboardingSurvey,
        integrations,
        brandColors,
        dashboardSettings,
        prebuildModules,
        aiText,
        dashboardSeenNotifications,
        onboarding,
        formCreator,
        mergeFields,
        landingPageCreator,
        landingPageSubdomain,
        checkoutCreator,
        checkoutsList,
        checkoutsTerms,
        checkoutsStripeAccount
    };

    const EvsReducers = { singleLeads, verificationFiles };

    return combineReducers<ApplicationState>(
        reduceMultiProductReducers<ApplicationState>(productReducers, { ...ApiReducers, ...EvsReducers })
    );
};

export const getRouterStore = (state: ApplicationState): RouterState => state.router;
export const getConfigStore = (store: ApplicationState): ConfigState => store.config;
